import React, { useCallback, useEffect, useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import LoginPage from './components/auth/LoginPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import { normalizeSession } from './auth/session';
import {
  BillingProcessDetailPage,
  BillingProcessesPage,
  BillingConfigurationPage,
  BillingCorrespondencesPage,
  PendingCustomerPage,
  PendingExpeditionsPage,
} from './pages/ExpeditionBillingPages';
import { getCurrentUser } from './services';

function App() {
  const isMobile = useMediaQuery('(max-width:900px)');
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [session, setSession] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    getCurrentUser()
      .then((currentSession) => {
        if (mounted) setSession(normalizeSession(currentSession));
      })
      .catch(() => {
        if (mounted) setSession(null);
      })
      .finally(() => {
        if (mounted) setAuthChecked(true);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [isMobile, location.pathname]);

  const handleLoginSuccess = useCallback((currentSession) => {
    setSession(currentSession?.user ? normalizeSession(currentSession) : currentSession);
    setAuthChecked(true);
  }, []);

  const protect = (element) => (
    <ProtectedRoute authChecked={authChecked} session={session}>
      {element}
    </ProtectedRoute>
  );

  if (location.pathname === '/login') {
    return (
      <Routes>
        <Route path="/login" element={session ? <Navigate replace to="/" /> : <LoginPage onLogin={handleLoginSuccess} />} />
        <Route path="*" element={<Navigate replace to="/login" />} />
      </Routes>
    );
  }

  return (
    <Box className="suite-shell">
      <Sidebar
        open={sidebarOpen}
        mobile={isMobile}
        collapsed={sidebarCollapsed}
        onClose={() => setSidebarOpen(false)}
      />
      <Box className="suite-main">
        <Topbar
          mobile={isMobile}
          onMenuClick={() => {
            if (isMobile) setSidebarOpen((value) => !value);
            else setSidebarCollapsed((value) => !value);
          }}
          sidebarCollapsed={sidebarCollapsed}
          sidebarOpen={sidebarOpen}
          session={session}
          title={getPageTitle(location.pathname)}
        />
        <Box component="main" className="suite-content">
          <Routes>
            <Route path="/" element={protect(<PendingExpeditionsPage />)} />
            <Route path="/pendientes/:customerCode" element={protect(<PendingCustomerPage />)} />
            <Route path="/procesos" element={protect(<BillingProcessesPage />)} />
            <Route path="/procesos/:processId" element={protect(<BillingProcessDetailPage />)} />
            <Route path="/configuracion" element={protect(<BillingConfigurationPage />)} />
            <Route path="/correspondencias" element={protect(<BillingCorrespondencesPage />)} />
            <Route path="*" element={<Navigate replace to="/" />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
}

function getPageTitle(pathname) {
  if (pathname === '/correspondencias') return 'Equivalencias del cliente';
  if (pathname === '/') return 'Expediciones pendientes';
  if (pathname.startsWith('/pendientes/')) return 'Expediciones del cliente';
  if (pathname.startsWith('/procesos/')) return 'Proceso de facturación';
  if (pathname === '/procesos') return 'Procesos de facturación';
  if (pathname === '/configuracion') return 'Configuración de facturación';
  return 'Facturación de expediciones';
}

export default App;
